import { styled, TablePagination as TablePaginationImpl } from "@mui/material";
// import { styled } from "@mui/system";
import Card from "components/Card";
import { ChainOptions } from "components/filters/ChainSelectFilter";
import Filters from "components/filters/Filters";
import MessagesTable from "components/messagesTable/MessagesTable";
import {
  useExecutedMessagesQuery,
  useLastFinalizedBlock,
  useLatestHeadUpdates,
  useSentMessagesQuery,
} from "hooks/messages";
import { SourceChainContext } from "hooks/sourceChain";
import CHAINS, { DESTINATION_CHAINS } from "lib/chains";
import NoSSR from "lib/NoSSR";

import {
  DefaultMessageTableFilters,
  MessageTableRow,
  PageWithHeader,
} from "lib/types";
import useQueryState, { dayQueryType } from "lib/useQueryState";
import { queryTypes, useQueryStates } from "next-usequerystate";
import { ChangeEvent, useContext } from "react";

// The Graph limits the skip param to 5000 so that's the max pages we can handle
const THE_GRAPH_MAX_SKIP = 5000;

const TablePagination = styled(TablePaginationImpl)({
  "& .MuiTablePagination-toolbar": {
    padding: 0,
  },
  ".MuiInputBase-root": {
    marginRight: "4px",
  },
}) as typeof TablePaginationImpl;

const Messages: PageWithHeader = () => {
  const sourceChain = useContext(SourceChainContext);

  // Filters

  const chainOptions: ChainOptions =
    DESTINATION_CHAINS[sourceChain].map((c) => ({
      id: c,
      name: CHAINS[c].name,
    })) || [];

  const [filters, setFilters] = useQueryStates({
    chain: queryTypes
      .array(queryTypes.stringEnum(chainOptions.map((c) => c.id)), " ")
      .withDefault(chainOptions.map((o) => o.id)),
    origin: queryTypes.string.withDefault(""),
    source: queryTypes.string.withDefault(""),
    target: queryTypes.string.withDefault(""),
    startDate: dayQueryType,
    endDate: dayQueryType,
  });

  const reset = async () => {
    await setFilters(DefaultMessageTableFilters);
    setCurrentPage(1);
  };

  // Pagination

  const [currentPage, setCurrentPage] = useQueryState(
    "page",
    queryTypes.integer.withDefault(1)
  );
  const [pageSize, setPageSize] = useQueryState(
    "pageCount",
    queryTypes.integer.withDefault(50)
  );

  if (currentPage < 1) {
    setCurrentPage(1);
  }

  if (currentPage > THE_GRAPH_MAX_SKIP / pageSize) {
    setCurrentPage(THE_GRAPH_MAX_SKIP / pageSize);
  }

  const handleChangePage = (_: unknown, page: number) => {
    setCurrentPage(page + 1);
  };
  const handleChangeRowsPerPage = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setPageSize(parseInt(e.target.value));
  };

  const labelDisplayedRows = () => {
    return `Page ${currentPage}`;
  };

  // Fetch data whenever pagination/filters change

  const [sentMessages, loadingSentMessages] = useSentMessagesQuery(
    currentPage,
    pageSize,
    filters,
    sourceChain
  );
  const [executedMessages] = useExecutedMessagesQuery(
    sentMessages,
    sourceChain
  );

  const [latestHeadUpdates] = useLatestHeadUpdates(sourceChain);

  const lastFinalizedBlock = useLastFinalizedBlock(sourceChain);

  const rows: MessageTableRow[] = sentMessages.map((msg) => [
    msg,
    (executedMessages.size > 0 && executedMessages.get(msg?.messageNonce)) ||
      null,
  ]);

  return (
    <>
      <div className="max-w">
        <NoSSR>
          <Filters
            filters={filters}
            setFilters={setFilters}
            chainOptions={chainOptions}
            reset={reset}
          />
          <MessagesTable
            loading={loadingSentMessages}
            rows={rows}
            latestHeadUpdates={latestHeadUpdates}
            lastFinalizedBlock={lastFinalizedBlock}
          />
          <TablePagination
            component="div"
            className="border-none"
            count={-1}
            page={currentPage - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={labelDisplayedRows}
            rowsPerPage={pageSize}
            nextIconButtonProps={{
              disabled:
                rows.length < pageSize ||
                currentPage >= THE_GRAPH_MAX_SKIP / pageSize,
            }}
            style={{ color: "white", border: " none" }}
          />
        </NoSSR>
      </div>
    </>
  );
};

Messages.header = "Messages";

export default Messages;
